import SelectBuscable from '@/shared/components/SelectBuscable'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useMemo } from "react";
import { Badge } from "@/shared/components/Badge";
import {
  Search,
  MoreVertical,
  Eye,
  CheckCircle,
  CheckCheck,
  FileText,
  AlertCircle,
  XCircle,
  MessageSquare,
  Paperclip,
  Truck,
  UserPlus,
} from "lucide-react";

// Custom Calendar icon with folded corner to match design
const CalendarFolded = ({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);
import { useNavigate } from "react-router-dom";
import { usePurchaseOrdersRH } from '@/hooks/usePurchaseOrdersRH';
import { purchaseOrdersRHService } from '@/features/rh/api/purchaseOrdersRH.service';
import type { PurchaseOrderRHFilters } from '@/types/purchaseOrderRH.types';
import { AddCommentModal } from '@/features/rh/components/rh/AddCommentModal';
import { ViewCommentsModal } from '@/features/rh/components/rh/ViewCommentsModal';
import { AssignSupplierModal } from '@/features/rh/components/rh/AssignSupplierModal';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import KpiCard from "@/features/rh/components/dashboard/KpiCard";
import { EsqueletoTarjetasTexto } from "@/shared/components/Esqueleto";

export const PurchaseOrdersRH: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [supplierFilter, setSupplierFilter] = useState<string>("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 20;

  // Comment modals state
  const [addCommentModal, setAddCommentModal] = useState<{
    isOpen: boolean;
    orderId: string;
    orderNumber: string;
  }>({ isOpen: false, orderId: "", orderNumber: "" });
  const [viewCommentsModal, setViewCommentsModal] = useState<{
    isOpen: boolean;
    orderId: string;
    orderNumber: string;
  }>({ isOpen: false, orderId: "", orderNumber: "" });
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [assignSupplierModal, setAssignSupplierModal] = useState<{
    isOpen: boolean;
    orderId: string;
    orderNumber: string;
  }>({ isOpen: false, orderId: "", orderNumber: "" });

  const filters: PurchaseOrderRHFilters = useMemo(
    () => ({
      page,
      limit,
      search: searchTerm || undefined,
      status: (statusFilter as any) || undefined,
    }),
    [page, limit, searchTerm, statusFilter],
  );

  const { data, isLoading, isError, error } = usePurchaseOrdersRH(filters);

  const orders = data?.data || [];
  const meta = data?.meta;
  const supplierOptions = useMemo(() => {
    const seen = new Set<string>();

    return orders.reduce<Array<{ value: string; label: string }>>(
      (acc, order) => {
        const supplier = order.supplier;
        if (!supplier?.id) return acc;

        const key = supplier.id;
        if (seen.has(key)) return acc;
        seen.add(key);

        acc.push({
          value: supplier.id,
          label: purchaseOrdersRHService.getSupplierName(supplier),
        });

        return acc;
      },
      [],
    );
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      const searchableFields = [
        order.po_number,
        `OC-${order.po_number.slice(-4)}`,
        purchaseOrdersRHService.getSupplierName(order.supplier),
        order.supplier?.rfc,
        order.delivery_address,
        order.notes,
        order.currency,
        order.cfdi_status || "Disponible",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableFields.includes(normalizedSearch);
      const matchesSupplier =
        !supplierFilter || order.supplier?.id === supplierFilter;
      const invoiceStatus = (order.cfdi_status || "Disponible").toLowerCase();
      const matchesInvoiceStatus =
        !invoiceStatusFilter ||
        invoiceStatus === invoiceStatusFilter.toLowerCase();

      return matchesSearch && matchesSupplier && matchesInvoiceStatus;
    });
  }, [orders, searchTerm, supplierFilter, invoiceStatusFilter]);

  const stats = useMemo(
    () => purchaseOrdersRHService.calculateStats(data?.data || []),
    [data],
  );

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setSupplierFilter("");
    setInvoiceStatusFilter("");
    setPage(1);
  };

  const hasActiveFilters =
    searchTerm || statusFilter || supplierFilter || invoiceStatusFilter;

  if (
    userHasPermission(
      "proveedores.administracion.ordenes_compra.consultar",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  const handleAddComment = async (comment: string) => {
    await purchaseOrdersRHService.addComment(addCommentModal.orderId, comment);
    setCommentSuccess(true);
    setTimeout(() => setCommentSuccess(false), 3000);
  };

  const openAddCommentModal = (orderId: string, orderNumber: string) => {
    setAddCommentModal({ isOpen: true, orderId, orderNumber });
  };

  const openViewCommentsModal = (orderId: string, orderNumber: string) => {
    setViewCommentsModal({ isOpen: true, orderId, orderNumber });
  };

  if (isError) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-red-100 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-red-700">
                Error al cargar órdenes
              </h3>
            </div>
            <p className="text-red-700">
              {error instanceof Error
                ? error.message
                : "Ocurrió un error al cargar las órdenes de compra"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-start my-6">
        <div>
          <TituloDePantalla>
            Órdenes de compra
          </TituloDePantalla>
          <p className="text-sm text-[#1E1E1F]">
            Administra aquí tus órdenes de compra
          </p>
        </div>
      </div>

      {/* Stats Cards — 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <KpiCard
          title="Total de órdenes de compras"
          value={stats.total}
          icon={<FileText />}
          variant="secondary"
          isLoading={isLoading}
        />
        <KpiCard
          title="Órdenes confirmadas"
          value={stats.confirmed}
          subtext={`${stats.confirmedPercentage.toFixed(1)}% del total`}
          icon={<CheckCheck />}
          variant="success"
          isLoading={isLoading}
        />
        <KpiCard
          title="Órdenes rechazadas"
          value={stats.rejected ?? 0}
          subtext={`${stats.rejectedPercentage?.toFixed(2) ?? "0.00"}% del total`}
          icon={<XCircle />}
          variant="error"
          isLoading={isLoading}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en orden de compra..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-3 py-2 !border !border-[#E5E7EB] rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Estatus"
            todos="Todos"
            value={statusFilter}
            onChange={(valor) => {
              setStatusFilter(valor);
              setPage(1);
            }}
            placeholderDeBusqueda="Buscar estatus…"
            canDelete={false}
            opciones={[
              { value: "draft", label: "Borrador" },
              { value: "confirmed", label: "Confirmada" },
              { value: "in_progress", label: "En Progreso" },
              { value: "completed", label: "Completada" },
              { value: "rejected", label: "Rechazada" },
            ]}
          />
        </div>

        {/* Supplier Filter */}
        <div className="relative">
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Proveedor"
            todos="Todos"
            value={supplierFilter}
            onChange={(valor) => {
              setSupplierFilter(valor);
              setPage(1);
            }}
            placeholderDeBusqueda="Buscar proveedor…"
            canDelete={false}
            opciones={supplierOptions}
          />
        </div>

        {/* Invoice Status Filter */}
        <div className="relative">
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Estado factura"
            todos="Todos"
            value={invoiceStatusFilter}
            onChange={(valor) => {
              setInvoiceStatusFilter(valor);
              setPage(1);
            }}
            placeholderDeBusqueda="Buscar estado…"
            canDelete={false}
            opciones={[
              { value: "disponible", label: "Disponible" },
              { value: "en proceso", label: "En proceso" },
              { value: "pagado", label: "Pagado" },
            ]}
          />
        </div>

        {/* Clear Button */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Orders Cards */}
      <div className="space-y-4">
        {isLoading ? (
          <EsqueletoTarjetasTexto cuantas={4} columnas="" campos={6} />
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#1E1E1F] mb-2">
              No se encontraron órdenes
            </h3>
            <p className="text-gray-600">
              {searchTerm
                ? "Intenta ajustar los filtros de búsqueda"
                : "Aún no hay órdenes de compra registradas"}
            </p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            return (
              <div
                key={order.id}
                className="bg-white rounded-lg border border-[#E5E7EB] hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Row 1: Header Info & Actions */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <FileText
                          className="w-7 h-7"
                          style={{ color: "#2563eb" }}
                        />
                      </div>

                      {/* Basic Info */}
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-medium text-[#1E1E1F]">
                            OC-{order.po_number.slice(-4)}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <CalendarFolded className="w-4 h-4" />
                            {purchaseOrdersRHService.formatDate(
                              order.created_at,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions & Status */}
                    <div className="flex items-center gap-3">
                      {/* Status Badges */}
                      <div className="flex items-center gap-2">
                        <Badge
                          color={purchaseOrdersRHService.getStatusColor(
                            order.status,
                          )}
                        >
                          {purchaseOrdersRHService.getStatusLabel(order.status)}
                        </Badge>

                        {order.fulfillment_status && (
                          <Badge
                            color={
                              order.fulfillment_status === "Surtido"
                                ? "verde"
                                : "gris"
                            }
                          >
                            {order.fulfillment_status}
                          </Badge>
                        )}

                        {order.payment_status && (
                          <Badge
                            color={
                              order.payment_status === "Pagado" ? "verde" : "gris"
                            }
                          >
                            {order.payment_status}
                          </Badge>
                        )}
                      </div>

                      {/* Action Menu */}
                      <div className="relative">
                        <button
                          onClick={() =>
                            setOpenMenuId(
                              openMenuId === order.id ? null : order.id,
                            )
                          }
                          className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-[#2563eb]" />
                        </button>

                        {openMenuId === order.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-1 z-20">
                              {/* Draft: Confirmar / Rechazar */}
                              {order.status === "draft" && (
                                <>
                                  <button
                                    onClick={() => {
                                      // Add confirm logic here
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    Confirmar
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Add reject logic here
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                    Rechazar
                                  </button>
                                </>
                              )}

                              {/* Confirmed: Agregar comentario, Adjuntar Factura, Adjuntar evidencia */}
                              {order.status === "confirmed" && (
                                <>
                                  <button
                                    onClick={() =>
                                      openAddCommentModal(
                                        order.id,
                                        order.po_number,
                                      )
                                    }
                                    className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <MessageSquare className="w-4 h-4 text-[#2563eb]" />
                                    Agregar comentario
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Add attach invoice logic here
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4 text-[#2563eb]" />
                                    Adjuntar Factura
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Paperclip className="w-4 h-4 text-[#2563eb]" />
                                    Adjuntar evidencia
                                  </button>
                                </>
                              )}

                              {/* Always visible: Ver comentarios, Ver detalle */}
                              <button
                                onClick={() =>
                                  openViewCommentsModal(
                                    order.id,
                                    order.po_number,
                                  )
                                }
                                className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4 text-[#2563eb]" />
                                Ver comentarios
                              </button>

                              {!order.supplier && (
                                <button
                                  onClick={() => {
                                    setAssignSupplierModal({
                                      isOpen: true,
                                      orderId: order.id,
                                      orderNumber: order.po_number,
                                    });
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <UserPlus className="w-4 h-4 text-[#2563eb]" />
                                  Asignar Proveedor
                                </button>
                              )}

                              <button
                                onClick={() => {
                                  navigate(`/rh/purchase-orders/${order.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-[#1E1E1F] hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4 text-[#2563eb]" />
                                Ver detalle
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Metrics Grid */}
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 lg:gap-6 cursor-pointer pl-11"
                    onClick={() => {
                      navigate(`/rh/purchase-orders/${order.id}`);
                      setOpenMenuId(null);
                    }}
                  >
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Proveedor</p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {order.supplier
                          ? purchaseOrdersRHService.getSupplierName(
                              order.supplier,
                            )
                          : "Sin proveedor"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fecha</p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {purchaseOrdersRHService.formatDate(
                          order.delivery_date,
                        )}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Cantidad solicitada
                      </p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {Math.trunc(
                          order.lines?.reduce(
                            (sum, l) => sum + (Number(l.quantity) || 0),
                            0,
                          ) ?? 0,
                        )}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Cantidad recibida
                      </p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {Math.trunc(
                          order.lines?.reduce(
                            (sum, l) =>
                              sum + (Number(l.quantity_received) || 0),
                            0,
                          ) ?? 0,
                        )}
                        /
                        {Math.trunc(
                          order.lines?.reduce(
                            (sum, l) => sum + (Number(l.quantity) || 0),
                            0,
                          ) ?? 0,
                        )}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Estado factura
                      </p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {order.cfdi_status || "Disponible"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">Moneda</p>
                      <p className="text-sm font-semibold text-[#1E1E1F]">
                        {order.currency}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Importe total
                      </p>
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-semibold text-[#1E1E1F]">
                          {purchaseOrdersRHService.formatCurrency(
                            order.total,
                            order.currency,
                          )}
                        </p>

                        {/* Feedback Indicators */}
                        <div className="flex gap-1.5 shrink-0">
                          {(order.invoices_count ?? 0) > 0 && (
                            <Badge
                              color="morado"
                              icon={<Paperclip className="w-3.5 h-3.5" />}
                            >
                              ({order.invoices_count})
                            </Badge>
                          )}
                          {(order.comments_count ?? 0) > 0 && (
                            <span
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewCommentsModal(
                                  order.id,
                                  order.po_number,
                                );
                              }}
                            >
                              <Badge
                                color="morado"
                                icon={<MessageSquare className="w-3.5 h-3.5" />}
                              >
                                ({order.comments_count})
                              </Badge>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between bg-white rounded-lg border border-[#E5E7EB] p-4">
          <div className="text-sm text-gray-600">
            Mostrando {(meta.page - 1) * meta.limit + 1} -{" "}
            {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}{" "}
            órdenes
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {meta.page} de {meta.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Success notification */}
      {commentSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <CheckCircle className="w-5 h-5" />
          Comentario agregado exitosamente
        </div>
      )}

      {/* Modals */}
      <AddCommentModal
        isOpen={addCommentModal.isOpen}
        onClose={() =>
          setAddCommentModal({ isOpen: false, orderId: "", orderNumber: "" })
        }
        onSubmit={handleAddComment}
        orderNumber={addCommentModal.orderNumber}
      />

      <ViewCommentsModal
        isOpen={viewCommentsModal.isOpen}
        onClose={() =>
          setViewCommentsModal({ isOpen: false, orderId: "", orderNumber: "" })
        }
        orderId={viewCommentsModal.orderId}
        orderNumber={viewCommentsModal.orderNumber}
      />

      <AssignSupplierModal
        isOpen={assignSupplierModal.isOpen}
        onClose={() =>
          setAssignSupplierModal({
            isOpen: false,
            orderId: "",
            orderNumber: "",
          })
        }
        onSuccess={() => window.location.reload()}
        orderId={assignSupplierModal.orderId}
        orderNumber={assignSupplierModal.orderNumber}
      />
    </div>
  );
};
